import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const Coach = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [ctxId, setCtxId] = useState("");
  const sidRef = useRef(`coach-${user?.id}-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/recordings").then(r => setRecordings(r.data)); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9 }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { role: "user", text: input };
    setMessages((m) => [...m, userMsg]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/coach/chat", { message: userMsg.text, session_id: sidRef.current, recording_id: ctxId || null });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed";
      setMessages((m) => [...m, { role: "assistant", text: detail, err: true }]);
    } finally { setBusy(false); }
  };

  if (user?.plan === "free") {
    return (
      <div className="min-h-screen flex items-center justify-center p-10">
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto mb-6 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px]">
            <Sparkles className="text-amber-500" size={22}/>
          </div>
          <div className="label-mono mb-3">/ locked · starter+</div>
          <h1 className="font-display text-3xl tightest font-semibold mb-4 leading-tight">1:1 AI Study Coach</h1>
          <p className="text-neutral-500 text-[0.9rem] mb-8 leading-relaxed">
            A socratic tutor that remembers every lesson you&apos;ve recorded. Available on Starter (£5) and Pro (£12).
          </p>
          <Link to="/pricing"><button className="btn-primary" data-testid="coach-upgrade">Unlock Coach <ArrowRight size={13}/></button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto h-screen flex flex-col">
      <header className="mb-6">
        <div className="label-mono mb-3">/ ai coach</div>
        <h1 className="font-display text-4xl tightest font-semibold leading-none">Ask me anything.</h1>
        <p className="text-neutral-500 mt-3 text-[0.92rem]">Optionally attach a lesson so I can reason about it directly.</p>
      </header>
      <div className="mb-4">
        <select className="input-cyber max-w-md" value={ctxId} onChange={(e)=>setCtxId(e.target.value)} data-testid="coach-context">
          <option value="">No lesson context</option>
          {recordings.map(r => <option key={r.id} value={r.id}>{r.subject} · {new Date(r.created_at).toLocaleDateString()}</option>)}
        </select>
      </div>
      <div ref={scrollRef} className="flex-1 glass p-6 overflow-y-auto space-y-4 mb-4" data-testid="coach-chat">
        {messages.length === 0 && (
          <div className="text-neutral-500 text-[0.86rem]">Hi {user?.display_name?.split(" ")[0]} — what should we work on first?</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] px-4 py-3 rounded-[4px] text-[0.88rem] leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-white text-black"
                  : m.err
                    ? "bg-rose-500/10 border border-rose-500/30 text-rose-200"
                    : "bg-white/[0.04] border-l-2 border-amber-500/60 text-neutral-200"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && <div className="text-neutral-500 text-[0.72rem] font-mono">thinking…</div>}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input className="input-cyber" value={input} onChange={(e)=>setInput(e.target.value)} placeholder="Ask a question…" data-testid="coach-input"/>
        <button type="submit" className="btn-primary" disabled={busy} data-testid="coach-send"><Send size={13}/></button>
      </form>
    </div>
  );
};

export default Coach;
